<template>
  <div class="player-view">
    <!-- Loading / Error -->
    <div v-if="bioLoading" class="page-loading">
      <div v-for="i in 3" :key="i" class="skeleton-row"></div>
    </div>
    <div v-else-if="bioError" class="sect sect--narrow">
      <div class="alert alert--error">{{ bioError }}</div>
    </div>

    <template v-else-if="player">
      <!-- Dateline -->
      <div class="player-dateline">
        <div class="player-dateline__inner">
          <span>The Player Desk</span>
          <span>
            Profile
            <span class="player-dateline__dot">♦</span>
            Career &amp; Season Log
          </span>
          <span class="player-dateline__right">
            <RouterLink :to="{ name: 'stats' }" class="link-ink">&larr; Leaderboards</RouterLink>
          </span>
        </div>
      </div>

      <!-- Hero -->
      <header class="hero">
        <p class="eyebrow">Player Profile</p>
        <h1 class="hero__name">
          {{ player.full_name }}
        </h1>
        <p v-if="player.nickname" class="hero__nickname">
          <em>&ldquo;{{ player.nickname }}&rdquo;</em>
        </p>
        <div class="hero__byline">
          <span class="byline__rule"></span>
          <span class="byline__tags">
            <span class="hero__tag">
              Bats
              <strong>{{ fmtHand(player.bats) }}</strong>
            </span>
            <span class="hero__tag-sep">&middot;</span>
            <span class="hero__tag">
              Throws
              <strong>{{ fmtHand(player.throws) }}</strong>
            </span>
            <template v-if="player.height_inches">
              <span class="hero__tag-sep">&middot;</span>
              <span class="hero__tag">
                Ht
                <strong>{{ fmtHeight(player.height_inches) }}</strong>
              </span>
            </template>
            <template v-if="player.weight">
              <span class="hero__tag-sep">&middot;</span>
              <span class="hero__tag">
                Wt
                <strong>{{ player.weight }} lbs</strong>
              </span>
            </template>
          </span>
          <span class="byline__rule"></span>
        </div>
        <hr class="rule rule--double" />
      </header>

      <!-- I. Biography -->
      <section class="sect" aria-label="Player Biography">
        <div class="sect__header">
          <div class="section-number">I</div>
          <div class="sect__titles">
            <p class="eyebrow">The Record &mdash; Biography</p>
            <h2 class="display-md">The man at the <em>plate.</em></h2>
          </div>
        </div>
        <hr class="rule" />

        <div class="bio-grid">
          <div class="bio-item">
            <span class="bio-label">Bats / Throws</span>
            <span class="bio-value numeric"
              >{{ fmtHand(player.bats) }} / {{ fmtHand(player.throws) }}</span
            >
          </div>
          <div v-if="player.birth_date" class="bio-item">
            <span class="bio-label">Born</span>
            <span class="bio-value">{{ fmtDate(player.birth_date) }}</span>
          </div>
          <div v-if="player.height_inches || player.weight" class="bio-item">
            <span class="bio-label">Height / Weight</span>
            <span class="bio-value numeric">
              <template v-if="player.height_inches">{{ fmtHeight(player.height_inches) }}</template>
              <template v-if="player.height_inches && player.weight">, </template>
              <template v-if="player.weight">{{ player.weight }} lbs</template>
            </span>
          </div>
          <div v-if="birthplace" class="bio-item">
            <span class="bio-label">Birthplace</span>
            <span class="bio-value">{{ birthplace }}</span>
          </div>
          <div v-if="player.debut_date" class="bio-item">
            <span class="bio-label">Debut</span>
            <span class="bio-value">{{ fmtDate(player.debut_date) }}</span>
          </div>
          <div v-if="player.last_game_date" class="bio-item">
            <span class="bio-label">Last Game</span>
            <span class="bio-value">{{ fmtDate(player.last_game_date) }}</span>
          </div>
        </div>
      </section>

      <hr class="rule rule--ox sect__divider" />

      <!-- II. Career & Season Log -->
      <section class="sect" aria-label="Player Stats">
        <div class="sect__header">
          <div class="section-number">II</div>
          <div class="sect__titles">
            <p class="eyebrow">
              The Back of the Card &mdash;
              {{
                hasBatting && hasPitching
                  ? 'Batting &amp; Pitching'
                  : hasBatting
                    ? 'Batting'
                    : 'Pitching'
              }}
            </p>
            <h2 class="display-md">Season by season, <em>line by line.</em></h2>
          </div>
        </div>
        <hr class="rule" />

        <!-- Tab bar (only if both stat types have data) -->
        <div v-if="hasBatting && hasPitching" class="tab-bar" role="tablist">
          <button
            class="tab-btn"
            :class="{ 'tab-btn--active': activeTab === 'batting' }"
            role="tab"
            :aria-selected="activeTab === 'batting'"
            @click="activeTab = 'batting'"
          >
            Batting
          </button>
          <button
            class="tab-btn"
            :class="{ 'tab-btn--active': activeTab === 'pitching' }"
            role="tab"
            :aria-selected="activeTab === 'pitching'"
            @click="activeTab = 'pitching'"
          >
            Pitching
          </button>
        </div>

        <!-- Stats loading -->
        <div v-if="statsLoading" class="loading-state">
          <div v-for="i in 5" :key="i" class="skeleton-row"></div>
        </div>

        <div v-else-if="statsError" class="alert alert--error">{{ statsError }}</div>

        <!-- Batting table -->
        <div v-else-if="activeTab === 'batting' && hasBatting" class="scorecard">
          <div class="scorecard__scroll">
            <table class="data-table" aria-label="Batting stats by season">
              <thead>
                <tr>
                  <th class="col-year col-fixed num">Year</th>
                  <th class="col-team col-fixed">Team</th>
                  <th v-for="col in battingCols" :key="col.key" class="num" :title="col.label">
                    {{ col.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in sortedBatting"
                  :key="`${row.season_year}-${row.team_id ?? i}`"
                  class="row"
                >
                  <td class="num year-cell numeric">{{ row.season_year ?? '—' }}</td>
                  <td class="team-cell">{{ row.team_abbreviation || '—' }}</td>
                  <td class="num numeric">{{ fmtInt(row.games) }}</td>
                  <td class="num numeric">{{ fmtInt(row.plate_appearances) }}</td>
                  <td class="num numeric">{{ fmtInt(row.at_bats) }}</td>
                  <td class="num numeric">{{ fmtInt(row.runs) }}</td>
                  <td class="num numeric">{{ fmtInt(row.hits) }}</td>
                  <td class="num numeric">{{ fmtInt(row.doubles) }}</td>
                  <td class="num numeric">{{ fmtInt(row.triples) }}</td>
                  <td class="num numeric">{{ fmtInt(row.home_runs) }}</td>
                  <td class="num numeric">{{ fmtInt(row.rbi) }}</td>
                  <td class="num numeric">{{ fmtInt(row.walks) }}</td>
                  <td class="num numeric">{{ fmtInt(row.strikeouts) }}</td>
                  <td class="num numeric">{{ fmtInt(row.stolen_bases) }}</td>
                  <td class="num numeric">{{ fmtAvg(row.batting_avg) }}</td>
                  <td class="num numeric">{{ fmtAvg(row.on_base_pct) }}</td>
                  <td class="num numeric">{{ fmtAvg(row.slugging_pct) }}</td>
                  <td class="num numeric col-highlight">{{ fmtAvg(row.ops) }}</td>
                  <td class="num numeric">{{ fmtAvg(row.woba) }}</td>
                  <td class="num numeric col-highlight">{{ fmtWrc(row.wrc_plus) }}</td>
                  <td class="num numeric">{{ fmtAvg(row.babip) }}</td>
                  <td class="num numeric">{{ fmtAvg(row.isolated_power) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="careerBatting">
                <tr class="career-row">
                  <td class="career-label">Career</td>
                  <td></td>
                  <td class="num numeric">{{ fmtInt(careerBatting.g) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.pa) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.ab) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.r) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.h) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.d) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.t) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.hr) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.rbi) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.bb) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.k) }}</td>
                  <td class="num numeric">{{ fmtInt(careerBatting.sb) }}</td>
                  <td class="num numeric">{{ fmtAvg(careerBatting.avg) }}</td>
                  <td class="num numeric">{{ fmtAvg(careerBatting.obp) }}</td>
                  <td class="num numeric">{{ fmtAvg(careerBatting.slg) }}</td>
                  <td class="num numeric col-highlight">{{ fmtAvg(careerBatting.ops) }}</td>
                  <td class="num numeric">{{ fmtAvg(careerBatting.woba) }}</td>
                  <td class="num numeric col-highlight">{{ fmtWrc(careerBatting.wrc_plus) }}</td>
                  <td class="num numeric">{{ fmtAvg(careerBatting.babip) }}</td>
                  <td class="num numeric">{{ fmtAvg(careerBatting.iso) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Pitching table -->
        <div v-else-if="activeTab === 'pitching' && hasPitching" class="scorecard">
          <div class="scorecard__scroll">
            <table class="data-table" aria-label="Pitching stats by season">
              <thead>
                <tr>
                  <th class="col-year col-fixed num">Year</th>
                  <th class="col-team col-fixed">Team</th>
                  <th v-for="col in pitchingCols" :key="col.key" class="num" :title="col.label">
                    {{ col.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in sortedPitching"
                  :key="`${row.season_year}-${row.team_id ?? i}`"
                  class="row"
                >
                  <td class="num year-cell numeric">{{ row.season_year ?? '—' }}</td>
                  <td class="team-cell">{{ row.team_abbreviation || '—' }}</td>
                  <td class="num numeric">{{ fmtInt(row.games) }}</td>
                  <td class="num numeric">{{ fmtInt(row.games_started) }}</td>
                  <td class="num numeric">{{ fmtInt(row.wins) }}</td>
                  <td class="num numeric">{{ fmtInt(row.losses) }}</td>
                  <td class="num numeric">{{ fmtInt(row.saves) }}</td>
                  <td class="num numeric">{{ fmtIP(row.innings_pitched) }}</td>
                  <td class="num numeric">{{ fmtInt(row.strikeouts) }}</td>
                  <td class="num numeric">{{ fmtInt(row.walks) }}</td>
                  <td class="num numeric">{{ fmtInt(row.home_runs) }}</td>
                  <td class="num numeric col-highlight">{{ fmtDec(row.era) }}</td>
                  <td class="num numeric col-highlight">{{ fmtDec(row.whip) }}</td>
                  <td class="num numeric">{{ fmtDec(row.fip) }}</td>
                  <td class="num numeric">{{ fmtDec(row.k_per_9) }}</td>
                  <td class="num numeric">{{ fmtDec(row.bb_per_9) }}</td>
                  <td class="num numeric">{{ fmtDec(row.k_per_bb) }}</td>
                  <td class="num numeric">{{ fmtPct(row.groundball_pct) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="careerPitching">
                <tr class="career-row">
                  <td class="career-label">Career</td>
                  <td></td>
                  <td class="num numeric">{{ fmtInt(careerPitching.g) }}</td>
                  <td class="num numeric">{{ fmtInt(careerPitching.gs) }}</td>
                  <td class="num numeric">{{ fmtInt(careerPitching.w) }}</td>
                  <td class="num numeric">{{ fmtInt(careerPitching.l) }}</td>
                  <td class="num numeric">{{ fmtInt(careerPitching.sv) }}</td>
                  <td class="num numeric">{{ fmtIP(careerPitching.ip) }}</td>
                  <td class="num numeric">{{ fmtInt(careerPitching.k) }}</td>
                  <td class="num numeric">{{ fmtInt(careerPitching.bb) }}</td>
                  <td class="num numeric">{{ fmtInt(careerPitching.hr) }}</td>
                  <td class="num numeric col-highlight">{{ fmtDec(careerPitching.era) }}</td>
                  <td class="num numeric col-highlight">{{ fmtDec(careerPitching.whip) }}</td>
                  <td class="num numeric">{{ fmtDec(careerPitching.fip) }}</td>
                  <td class="num numeric">{{ fmtDec(careerPitching.k9) }}</td>
                  <td class="num numeric">{{ fmtDec(careerPitching.bb9) }}</td>
                  <td class="num numeric">{{ fmtDec(careerPitching.kbb) }}</td>
                  <td class="num numeric">{{ fmtPct(careerPitching.groundball_pct) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <p v-else-if="!statsLoading" class="empty-state">
          <em>The record is silent.</em> No season-by-season stats available for this player.
        </p>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { referenceApi } from '@/api/reference'
import { statsApi } from '@/api/stats'
import type { PlayerDetail } from '@/api/reference'
import type { BattingStatsRow, PitchingStatsRow } from '@/api/stats'
import type { ApiError } from '@/types/api'

const route = useRoute()
const rawId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
const playerId = parseInt(rawId, 10)

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const player = ref<PlayerDetail | null>(null)
const bioLoading = ref(true)
const bioError = ref<string | null>(null)

const battingRows = ref<BattingStatsRow[]>([])
const pitchingRows = ref<PitchingStatsRow[]>([])
const statsLoading = ref(true)
const statsError = ref<string | null>(null)

type StatTab = 'batting' | 'pitching'
const activeTab = ref<StatTab>('batting')

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const hasBatting = computed(() => battingRows.value.length > 0)
const hasPitching = computed(() => pitchingRows.value.length > 0)

const sortedBatting = computed(() =>
  [...battingRows.value].sort((a, b) => (a.season_year ?? 0) - (b.season_year ?? 0))
)

const sortedPitching = computed(() =>
  [...pitchingRows.value].sort((a, b) => (a.season_year ?? 0) - (b.season_year ?? 0))
)

const birthplace = computed(() => {
  if (!player.value) return null
  const { birth_city, birth_state, birth_country } = player.value
  return [birth_city, birth_state, birth_country].filter(Boolean).join(', ') || null
})

// PA-weighted mean for a rate stat. Matches FanGraphs/BB-Ref methodology;
// recalculating from raw counting stats would require league-average data.
function _paWeighted(
  rows: BattingStatsRow[],
  key: 'woba' | 'wrc_plus',
  totalPA: number
): number | null {
  return totalPA > 0
    ? rows.reduce((acc, r) => acc + (r[key] ?? 0) * (r.plate_appearances ?? 0), 0) / totalPA
    : null
}

// Extracted to keep careerBatting complexity within ESLint limits.
function _battingRates(
  ab: number,
  h: number,
  d: number,
  t: number,
  hr: number,
  bb: number,
  hbp: number,
  sf: number,
  k: number
) {
  const avg = ab > 0 ? h / ab : null
  const obpDen = ab + bb + hbp + sf
  const obp = obpDen > 0 ? (h + bb + hbp) / obpDen : null
  const slg = ab > 0 ? (h - d - t - hr + 2 * d + 3 * t + 4 * hr) / ab : null
  const ops = obp != null && slg != null ? obp + slg : null
  const babipDen = ab - k - hr + sf
  const babip = babipDen > 0 ? (h - hr) / babipDen : null
  const iso = slg != null && avg != null ? slg - avg : null
  return { avg, obp, slg, ops, babip, iso }
}

const careerBatting = computed(() => {
  const rows = sortedBatting.value
  if (rows.length === 0) return null

  const s = (key: keyof BattingStatsRow) =>
    rows.reduce((acc, r) => acc + ((r[key] as number) ?? 0), 0)

  const g = s('games'),
    pa = s('plate_appearances'),
    ab = s('at_bats')
  const r = s('runs'),
    h = s('hits'),
    d = s('doubles'),
    t = s('triples')
  const hr = s('home_runs'),
    rbi = s('rbi'),
    bb = s('walks'),
    k = s('strikeouts')
  const sb = s('stolen_bases'),
    hbp = s('hit_by_pitch'),
    sf = s('sacrifice_flies')

  const { avg, obp, slg, ops, babip, iso } = _battingRates(ab, h, d, t, hr, bb, hbp, sf, k)
  const woba = _paWeighted(rows, 'woba', pa)
  const wrc_plus = _paWeighted(rows, 'wrc_plus', pa)

  return {
    g,
    pa,
    ab,
    r,
    h,
    d,
    t,
    hr,
    rbi,
    bb,
    k,
    sb,
    avg,
    obp,
    slg,
    ops,
    woba,
    wrc_plus,
    babip,
    iso
  }
})

const careerPitching = computed(() => {
  const rows = sortedPitching.value
  if (rows.length === 0) return null

  const s = (key: keyof PitchingStatsRow) =>
    rows.reduce((acc, r) => acc + ((r[key] as number) ?? 0), 0)

  const g = s('games'),
    gs = s('games_started'),
    w = s('wins'),
    l = s('losses')
  const sv = s('saves'),
    ip = s('innings_pitched'),
    k = s('strikeouts')
  const bb = s('walks'),
    hr = s('home_runs'),
    er = s('earned_runs'),
    h = s('hits')

  const era = ip > 0 ? (er / ip) * 9 : null
  const whip = ip > 0 ? (h + bb) / ip : null
  const k9 = ip > 0 ? (k / ip) * 9 : null
  const bb9 = ip > 0 ? (bb / ip) * 9 : null
  const kbb = bb > 0 ? k / bb : null
  const fip =
    ip > 0 ? rows.reduce((acc, r) => acc + (r.fip ?? 0) * (r.innings_pitched ?? 0), 0) / ip : null
  const groundball_pct =
    ip > 0
      ? rows.reduce((acc, r) => acc + (r.groundball_pct ?? 0) * (r.innings_pitched ?? 0), 0) / ip
      : null

  return { g, gs, w, l, sv, ip, k, bb, hr, era, whip, fip, k9, bb9, kbb, groundball_pct }
})

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const battingCols = [
  { key: 'games', label: 'G' },
  { key: 'plate_appearances', label: 'PA' },
  { key: 'at_bats', label: 'AB' },
  { key: 'runs', label: 'R' },
  { key: 'hits', label: 'H' },
  { key: 'doubles', label: '2B' },
  { key: 'triples', label: '3B' },
  { key: 'home_runs', label: 'HR' },
  { key: 'rbi', label: 'RBI' },
  { key: 'walks', label: 'BB' },
  { key: 'strikeouts', label: 'K' },
  { key: 'stolen_bases', label: 'SB' },
  { key: 'batting_avg', label: 'AVG' },
  { key: 'on_base_pct', label: 'OBP' },
  { key: 'slugging_pct', label: 'SLG' },
  { key: 'ops', label: 'OPS' },
  { key: 'woba', label: 'wOBA' },
  { key: 'wrc_plus', label: 'wRC+' },
  { key: 'babip', label: 'BABIP' },
  { key: 'isolated_power', label: 'ISO' }
]

const pitchingCols = [
  { key: 'games', label: 'G' },
  { key: 'games_started', label: 'GS' },
  { key: 'wins', label: 'W' },
  { key: 'losses', label: 'L' },
  { key: 'saves', label: 'SV' },
  { key: 'innings_pitched', label: 'IP' },
  { key: 'strikeouts', label: 'K' },
  { key: 'walks', label: 'BB' },
  { key: 'home_runs', label: 'HR' },
  { key: 'era', label: 'ERA' },
  { key: 'whip', label: 'WHIP' },
  { key: 'fip', label: 'FIP' },
  { key: 'k_per_9', label: 'K/9' },
  { key: 'bb_per_9', label: 'BB/9' },
  { key: 'k_per_bb', label: 'K/BB' },
  { key: 'groundball_pct', label: 'GB%' }
]

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function fetchBio(): Promise<void> {
  try {
    player.value = await referenceApi.getPlayer(playerId)
  } catch (err: unknown) {
    const apiErr = err as ApiError
    bioError.value =
      apiErr.detail ||
      (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
      'Failed to load player'
  } finally {
    bioLoading.value = false
  }
}

async function fetchStats(): Promise<void> {
  try {
    const [batting, pitching] = await Promise.all([
      statsApi.getBatting({
        player_id: playerId,
        aggregation_period: 'season',
        split: 'total',
        limit: 200
      }),
      statsApi.getPitching({
        player_id: playerId,
        aggregation_period: 'season',
        split: 'total',
        limit: 200
      })
    ])
    battingRows.value = batting.data
    pitchingRows.value = pitching.data
    // Default to whichever tab has data; batting takes priority
    if (!hasBatting.value && hasPitching.value) {
      activeTab.value = 'pitching'
    }
  } catch (err: unknown) {
    const apiErr = err as ApiError
    statsError.value =
      apiErr.detail ||
      (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
      'Failed to load stats'
  } finally {
    statsLoading.value = false
  }
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function fmtHand(val: string): string {
  const map: Record<string, string> = { left: 'L', right: 'R', switch: 'S', unknown: '?' }
  return map[val] ?? val
}

function fmtHeight(inches: number): string {
  const ft = Math.floor(inches / 12)
  const i = inches % 12
  return `${ft}'${i}"`
}

function fmtDate(val: string): string {
  return new Date(val + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function fmtInt(val: number | null): string {
  return val == null ? '—' : val.toLocaleString()
}

function fmtAvg(val: number | null): string {
  if (val == null) return '—'
  return val.toFixed(3).replace(/^0/, '')
}

function fmtDec(val: number | null): string {
  return val == null ? '—' : val.toFixed(2)
}

// innings_pitched is stored as outs/3.0 (decimal); convert to baseball notation (e.g. 200.1)
function fmtIP(val: number | null): string {
  if (val == null) return '—'
  const whole = Math.floor(val)
  const outs = Math.round((val - whole) * 3) // 0, 1, or 2
  return `${whole}.${outs}`
}

function fmtWrc(val: number | null): string {
  return val == null ? '—' : Math.round(val).toString()
}

function fmtPct(val: number | null): string {
  if (val == null) return '—'
  return (val * 100).toFixed(1) + '%'
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(async () => {
  if (isNaN(playerId) || playerId <= 0) {
    bioError.value = 'Invalid player ID'
    bioLoading.value = false
    statsLoading.value = false
    return
  }
  await fetchBio()
  if (!bioError.value) {
    fetchStats()
  }
})
</script>

<style scoped>
.player-view {
  min-height: 100vh;
  padding-top: 4.75rem;
  color: var(--ink);
}

/* ─── Dateline ──────────────────────────────────────────────────────── */
.player-dateline {
  border-top: var(--rule-thin) solid var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  background: var(--paper);
}

.player-dateline__inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.55rem 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1.5rem;
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-medium);
  text-align: center;
}

.player-dateline__inner > span:first-child {
  text-align: left;
}
.player-dateline__right {
  text-align: right;
}

.player-dateline__dot {
  display: inline-block;
  margin: 0 0.6em;
  color: var(--ox);
  transform: translateY(-1px);
}

.player-dateline__right .link-ink {
  font-family: var(--font-ui);
  letter-spacing: 0.22em;
  font-size: 0.68rem;
  text-transform: uppercase;
}

/* ─── Hero ──────────────────────────────────────────────────────────── */
.hero {
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem 1.25rem;
}

.hero__name {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'SOFT' 30,
    'wght' 700;
  font-size: clamp(2.75rem, 7vw, 5.5rem);
  line-height: 0.95;
  letter-spacing: -0.03em;
  color: var(--ink);
  margin: 0.75rem 0 0.25rem;
}

.hero__nickname {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 72,
    'wght' 500;
  font-size: clamp(1.15rem, 2vw, 1.5rem);
  color: var(--ox);
  margin-bottom: 1.25rem;
}

.hero__nickname em {
  font-style: italic;
}

.hero__byline {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.byline__rule {
  flex: 1;
  height: 1px;
  background: var(--ink);
  max-width: 80px;
  min-width: 24px;
}

.byline__tags {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-family: var(--font-ui);
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-medium);
  flex-wrap: wrap;
}

.hero__tag strong {
  font-family: var(--font-data);
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--ink);
  margin-left: 0.35rem;
}

.hero__tag-sep {
  color: var(--ox);
}

/* ─── Sections ──────────────────────────────────────────────────────── */
.sect {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2.5rem 2rem 3rem;
}

.sect--narrow {
  padding: 4rem 2rem;
}

.sect__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1.5rem;
  align-items: start;
  margin-bottom: 0.5rem;
}

.sect__titles .eyebrow {
  margin-bottom: 0.65rem;
}

.sect__titles em {
  font-style: italic;
  color: var(--ox);
}

.sect__divider {
  max-width: 1400px;
  margin: 0 auto;
}

/* ─── Bio grid ──────────────────────────────────────────────────────── */
.bio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 1.75rem 2.5rem;
  margin-top: 1.5rem;
  padding: 1.75rem 1.75rem;
  background: var(--paper-deep);
  border: var(--rule-thin) solid var(--ink);
  box-shadow: var(--shadow-press);
}

.bio-item {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  border-left: var(--rule-thick) solid var(--ox);
  padding-left: 1rem;
}

.bio-label {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

.bio-value {
  font-family: var(--font-editorial);
  font-size: 1.05rem;
  color: var(--ink);
  font-weight: 500;
}

/* ─── Tab bar ───────────────────────────────────────────────────────── */
.tab-bar {
  display: flex;
  gap: 0;
  border-bottom: var(--rule-thin) solid var(--ink);
  margin: 1.25rem 0 1.25rem;
}

.tab-btn {
  font-family: var(--font-ui);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  padding: 0.8rem 1.5rem;
  background: transparent;
  color: var(--ink-faint);
  border: none;
  border-bottom: var(--rule-thick) solid transparent;
  cursor: pointer;
  transition:
    color 0.18s ease,
    border-color 0.18s ease;
  margin-bottom: calc(var(--rule-thick) * -1 + var(--rule-thin) * -1);
}

.tab-btn:hover {
  color: var(--ink);
}

.tab-btn--active {
  color: var(--ink);
  border-bottom-color: var(--ox);
}

/* ─── Scorecard ─────────────────────────────────────────────────────── */
.scorecard {
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
  box-shadow: var(--shadow-press);
  margin-top: 0.75rem;
}

.scorecard__scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.data-table {
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  background: var(--paper);
  font-family: var(--font-editorial);
  font-size: 0.9rem;
  color: var(--ink);
}

.data-table thead {
  background: var(--paper-deep);
  border-bottom: var(--rule-med) solid var(--ink);
}

.data-table th {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-medium);
  padding: 0.7rem 0.65rem;
  white-space: nowrap;
  text-align: left;
}

.data-table th.num {
  text-align: right;
}

.data-table tbody tr {
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
  transition: background 0.15s ease;
}

.data-table tbody tr:last-child {
  border-bottom: none;
}

.data-table tbody tr:hover td {
  background: var(--paper-deep);
}

.data-table td {
  padding: 0.55rem 0.65rem;
  color: var(--ink);
  white-space: nowrap;
  vertical-align: baseline;
}

.col-year {
  width: 5rem;
}

.col-team {
  width: 4.5rem;
}

.year-cell {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 600;
  font-style: italic;
  font-size: 1rem;
  color: var(--ink);
}

.team-cell {
  font-family: var(--font-data);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  text-transform: uppercase;
}

.col-highlight {
  color: var(--ox);
  font-weight: 600;
}

.num {
  text-align: right;
}

/* ─── Career totals row ─────────────────────────────────────────────── */
.career-row td {
  border-top: var(--rule-med) solid var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  background: var(--ox-10);
  padding-top: 0.75rem;
  padding-bottom: 0.85rem;
  color: var(--ox);
  font-weight: 700;
}

.career-row .col-highlight {
  color: var(--ox-deep);
}

.career-label {
  font-family: var(--font-ui);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--ox);
}

/* ─── Loading / alert / empty ───────────────────────────────────────── */
.page-loading {
  max-width: 1400px;
  margin: 5rem auto;
  padding: 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 1rem 0;
}

.skeleton-row {
  height: 2.25rem;
  background: linear-gradient(
    90deg,
    var(--paper-deep) 25%,
    var(--paper-edge) 50%,
    var(--paper-deep) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border: var(--rule-thin) solid var(--paper-edge);
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.alert {
  padding: 0.9rem 1.15rem;
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  margin: 1rem 0;
  border: var(--rule-thin) solid var(--ox);
  background: var(--ox-10);
  color: var(--ox-deep);
}

.empty-state {
  font-family: var(--font-editorial);
  font-style: italic;
  font-size: 1.05rem;
  color: var(--ink-faint);
  text-align: center;
  padding: 3rem 1rem;
  max-width: 54ch;
  margin: 0 auto;
}

.empty-state em {
  color: var(--ox);
  font-style: italic;
}

/* ─── Responsive ────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .hero {
    padding: 2.25rem 1.25rem 1rem;
  }
  .sect {
    padding: 2rem 1.25rem;
  }
  .sect__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  .bio-grid {
    padding: 1.25rem;
  }
}

@media (max-width: 700px) {
  .player-dateline__inner {
    grid-template-columns: 1fr;
    gap: 0.3rem;
    text-align: center;
  }
  .player-dateline__inner > span:first-child,
  .player-dateline__right {
    text-align: center;
  }

  .hero__byline {
    flex-direction: column;
    align-items: flex-start;
  }
  .byline__rule {
    display: none;
  }
}

@media (max-width: 420px) {
  .hero__name {
    font-size: 2.5rem;
  }
  .sect {
    padding: 1.75rem 1rem;
  }
  .bio-grid {
    grid-template-columns: 1fr;
    padding: 1rem;
  }
}
</style>
